import { useVODStore } from '@/shared/store'
import {
    selectSetActualSeason,
    selectSetItemDetails,
    selectSetLastFocusedItem,
    selectSetVodRowItemType,
} from '@/shared/store/storeSelectors/vodSelectors'
import { useAnalytics } from '@/app/context/AnalyticsContext/AnalyticsContext'
import { debounce, parseAspectRatioToNumber } from '@/shared/lib'
import {
    channelType,
    cutvType,
    debounceSpeedMedium,
    homePagePath,
    vodContentType,
    VodPageDetailsType,
    vodPagePath,
    VodPageType,
} from '@/shared/constants'
import { pageType } from '@/shared/store/types'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTopMenu } from '@/widgets/TopMenu/hooks/useTopMenu'

import { getUseVodItemsDetails } from '@/shared/getContainer/getUseVodItemsDetails'
import { getVODExternalLinkHandlerHook } from '@/shared/getContainer/getUseExternalLinkHandlerHook'
import { useEffect, useState } from 'react'
import {
    seriesPagePath,
    SeriesPageType,
} from '@/shared/constants/seriesConstants'
import { getStreamIQConstants } from '@/shared/getContainer/getStreamIQConstants'

const getImageDimensions = (
    imageUrl: string
): Promise<{ width: number; height: number }> =>
    new Promise((resolve, reject) => {
        const img = new Image() // Create a new image element
        img.src = imageUrl // Set the image URL

        // Wait for the image to load
        img.onload = () => {
            resolve({ width: img.naturalWidth, height: img.naturalHeight })
        }

        // Handle any errors
        img.onerror = (error) => {
            reject(new Error(`Failed to load image at ${imageUrl}: ${error}`))
        }
    })

const useExternalLinkHandler = await getVODExternalLinkHandlerHook()
const useItemDetails = await getUseVodItemsDetails()
const constant = await getStreamIQConstants()
const { EPISODES_TYPE, MOVIE_TYPE, SHOW_TYPE } = constant || {}

export const useVodItemViewLogic = (
    item: VodItem,
    handleGoToCategoryButtonClick: (
        itemId: string,
        title: string,
        type: pageType
    ) => void
) => {
    const { handleExternalLink } = useExternalLinkHandler()

    const { details, getItemDetails } = useItemDetails()
    const setItemDetails = useVODStore(selectSetItemDetails)
    const setVodRowItemType = useVODStore(selectSetVodRowItemType)
    const setActualSeason = useVODStore(selectSetActualSeason)
    const setLastFocusedItem = useVODStore(selectSetLastFocusedItem)
    const { handleVodClick, handleSeriesClick } = useTopMenu()
    const Analytics = useAnalytics()
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const [imageDimensions, setImageDimensions] = useState({
        width: 0,
        height: 0,
    })
    const {
        label,
        assetType,
        productId,
        title,
        imageUrl,
        externalUri,
        duration,
        position,
        aspectRatio,
        channelId,
        id,
        serviceName,
        type,
    } = item

    const path = () => {
        if (pathname === seriesPagePath) {
            return seriesPagePath
        } else if (pathname === homePagePath && type === 'show') {
            return seriesPagePath
        } else if (pathname === vodPagePath || pathname === homePagePath) {
            return vodPagePath
        }

        return homePagePath
    }

    const watchingProgress =
        duration && position ? (position / (duration / 1000)) * 100 : 0
    const itemAspectRatio = parseAspectRatioToNumber(aspectRatio || '')

    const showDetails = assetType
        ? [
              ...vodContentType,
              cutvType,
              MOVIE_TYPE,
              SHOW_TYPE,
              EPISODES_TYPE,
          ].includes(assetType)
        : false

    const pageType = path() === seriesPagePath ? SeriesPageType : VodPageType

    const navigateToCategory = () => {
        if (productId) {
            handleGoToCategoryButtonClick(productId, title, pageType)
            Analytics?.analyticsLogEvent('vod_item_click', { productId, title })
            navigate(path())

            if (pageType === SeriesPageType) {
                handleSeriesClick(false)
            }
            if (pageType === VodPageType) {
                handleVodClick(false)
            }
        }
    }

    const showItemDetails = () => {
        setItemDetails({
            productId: item.productId,
            assetType: item.assetType,
            id: `vod:${item.productId}`,
            offers: details?.offers?.length ? details.offers : [],
            serviceName,
        })
        if (productId) {
            handleGoToCategoryButtonClick(productId, title, VodPageDetailsType)
        }

        if (pageType === SeriesPageType) {
            handleSeriesClick(false)
        }
        if (pageType === VodPageType) {
            handleVodClick(false)
        }

        navigate(path())
        setActualSeason(null)
    }

    const handleItemClick = (
        focusKey?: string,
        shouldMemorizeLastFocusedItem?: boolean
    ) => {
        if (assetType !== 'external' && assetType !== 'web') {
            setVodRowItemType(assetType || '')
        }
        if (assetType === 'cat') {
            navigateToCategory()
        } else if (externalUri) {
            // toya case. go to channel
            handleExternalLink(externalUri)
        } else if (!externalUri && channelId) {
            // streamIQ case, go to channel
            handleExternalLink(channelId)
        } else if (assetType === channelType) {
            // streamIQ case, go to channel
            handleExternalLink(id)
        } else {
            showItemDetails()
        }

        shouldMemorizeLastFocusedItem &&
            focusKey &&
            setLastFocusedItem(focusKey)
    }

    const handleMouseEnter = debounce(() => {
        if (showDetails) {
            getItemDetails({
                productId,
                assetType,
                id: `vod:${productId}` || '',
                serviceName: serviceName || '',
            })
        }
    }, debounceSpeedMedium)

    const handleMouseLeave = () => {
        handleMouseEnter.cancel()
    }

    useEffect(() => {
        getImageDimensions(imageUrl).then((res) => {
            setImageDimensions(res)
        })
    }, [showDetails])

    return {
        itemAspectRatio,
        handleMouseEnter,
        handleMouseLeave,
        handleItemClick,
        details,
        imageUrl,
        title,
        label,
        watchingProgress,
        imageDimensions,
    }
}
